import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ChevronLeft, ChevronRight, LucideIconData } from 'lucide-angular';
import { BadgeComponent } from '../../../../shared/ui/badge/badge.component';
import { ProductCardComponent } from '../../../../shared/ui/product-card/product-card.component';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import type { AddToCartEvent, Product } from '../../../../shared/models/product.model';
import type { StarterGuideItem } from '../../models/game-section.model';

const SLOT_STEP_FALLBACK = 216;

@Component({
  selector: 'app-starter-guide-group-carousel',
  imports: [ProductCardComponent, BadgeComponent, IconComponent],
  template: `
    <div class="sg-carousel__wrapper">
      <button
        class="sg-carousel__arrow sg-carousel__arrow--prev"
        type="button"
        aria-label="Anterior"
        (click)="scrollPrev()"
      >
        <app-icon [icon]="chevronLeft" [size]="20" />
      </button>

      <div class="sg-carousel__track" #track>
        @for (item of items(); track item.product.id) {
          <div class="sg-carousel__slot">
            <div class="sg-carousel__badge-slot">
              @if (item.badgeLabel) {
                <app-badge [variant]="item.badgeVariant ?? 'highlight'">{{ item.badgeLabel }}</app-badge>
              }
            </div>
            <app-product-card
              [product]="item.product"
              (zoomOpen)="zoomOpen.emit($event)"
              (addToCart)="addToCart.emit($event)"
              (notifyRequest)="notifyRequest.emit($event)"
              (notifyCancel)="notifyCancel.emit($event)"
              (cardClick)="cardClick.emit($event)"
              (reserveClick)="reserveClick.emit($event)"
            />
          </div>
        }
      </div>

      <button
        class="sg-carousel__arrow sg-carousel__arrow--next"
        type="button"
        aria-label="Siguiente"
        (click)="scrollNext()"
      >
        <app-icon [icon]="chevronRight" [size]="20" />
      </button>
    </div>
  `,
  styles: `
    .sg-carousel__wrapper {
      position: relative;
      display: flex;
      align-items: center;
      gap: var(--space-3);
    }

    .sg-carousel__track {
      --carousel-columns: 4;
      display: flex;
      gap: var(--space-4);
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      -ms-overflow-style: none;
      flex: 1;
      padding-block: var(--space-2);
    }

    .sg-carousel__track::-webkit-scrollbar {
      display: none;
    }

    .sg-carousel__slot {
      flex: 0 0 calc(
        (100% - (var(--carousel-columns) - 1) * var(--space-4))
        / var(--carousel-columns)
      );
      scroll-snap-align: start;
      display: flex;
      flex-direction: column;
      gap: var(--space-2);
    }

    .sg-carousel__badge-slot {
      min-height: 1.375rem;
    }

    .sg-carousel__arrow {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.25rem;
      height: 2.25rem;
      border-radius: var(--radius-button);
      border: 1px solid var(--color-border-primary);
      background: var(--color-surface-primary);
      color: var(--color-text-secondary);
      cursor: pointer;
      transition: background var(--transition-fast), color var(--transition-fast);
    }

    .sg-carousel__arrow:hover {
      background: var(--color-surface-secondary);
      color: var(--color-text-primary);
    }

    @media (max-width: 1024px) {
      .sg-carousel__track { --carousel-columns: 3; }
    }

    @media (max-width: 768px) {
      .sg-carousel__track { --carousel-columns: 2; }
    }

    @media (max-width: 640px) {
      .sg-carousel__arrow { display: none; }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class StarterGuideGroupCarouselComponent {
  items = input.required<StarterGuideItem[]>();
  zoomOpen = output<Product>();
  addToCart = output<AddToCartEvent>();
  // El starter guide muestra a propósito productos agotados: la card ya pinta
  // "Agotado" y la campana, pero sin propagar estos dos outputs el aviso de
  // stock sería un botón muerto.
  notifyRequest = output<Product>();
  notifyCancel = output<Product>();
  // La card emite `cardClick` al pulsar cualquier zona no interactiva (texto,
  // precio, hueco) y `reserveClick` en el CTA de precompra. Sin propagarlos, la
  // tarjeta de la guía no lleva a la ficha y «Reservar» queda muerto.
  cardClick = output<Product>();
  reserveClick = output<Product>();

  @ViewChild('track') private trackRef!: ElementRef<HTMLElement>;

  protected readonly chevronLeft: LucideIconData = ChevronLeft;
  protected readonly chevronRight: LucideIconData = ChevronRight;

  protected readonly currentIndex = signal(0);
  protected readonly visibleCount = signal(1);
  protected readonly totalItems = computed(() => this.items().length);
  protected readonly maxIndex = computed(() =>
    Math.max(0, this.totalItems() - this.visibleCount()),
  );

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    afterNextRender(() => {
      const el = this.trackRef?.nativeElement;
      if (!el) return;

      this.updateVisibleCount(el);

      const onScroll = (): void => this.onTrackScroll(el);
      el.addEventListener('scroll', onScroll, { passive: true });

      let resizeObserver: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => this.updateVisibleCount(el));
        resizeObserver.observe(el);
      }

      this.destroyRef.onDestroy(() => {
        el.removeEventListener('scroll', onScroll);
        resizeObserver?.disconnect();
      });
    });
  }

  protected scrollPrev(): void {
    if (!this.isBrowser) return;
    if (this.currentIndex() === 0) {
      this.scrollToIndex(this.maxIndex());
      return;
    }
    this.scrollToIndex(Math.max(0, this.currentIndex() - this.visibleCount()));
  }

  protected scrollNext(): void {
    if (!this.isBrowser) return;
    const next = this.currentIndex() + this.visibleCount();
    if (next > this.maxIndex()) {
      this.scrollToIndex(0);
      return;
    }
    this.scrollToIndex(next);
  }

  private scrollToIndex(index: number): void {
    const el = this.trackRef?.nativeElement;
    if (!el) return;
    const clamped = Math.max(0, Math.min(index, this.maxIndex()));
    const slotStep = this.getSlotStep();
    el.scrollTo({ left: clamped * slotStep, behavior: 'smooth' });
    this.currentIndex.set(clamped);
  }

  private onTrackScroll(el: HTMLElement): void {
    const slotStep = this.getSlotStep();
    const rawIndex = Math.round(el.scrollLeft / slotStep);
    const clamped = Math.max(0, Math.min(rawIndex, this.maxIndex()));
    this.currentIndex.set(clamped);
  }

  private updateVisibleCount(el: HTMLElement): void {
    const slotStep = this.getSlotStep();
    this.visibleCount.set(Math.max(1, Math.floor(el.clientWidth / slotStep)));
  }

  private getSlotStep(): number {
    const track = this.trackRef?.nativeElement;
    const firstSlot = track?.firstElementChild as HTMLElement | null;
    if (!firstSlot) return SLOT_STEP_FALLBACK;
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    return firstSlot.offsetWidth + gap;
  }
}
